// Full-page "end this enrollment" flow. Replaces the modal that lived
// on the student detail page. On successful submit shows a toast and
// navigates back to the student profile.
//
// Why a page instead of a modal: the flow has 5 stacked sections
// (details / plans / deposit / money preview / family script) plus a
// prorate math strip. That's a workflow, not a quick action. Modals
// fight it at every viewport; a page scrolls cleanly and deep-links.

import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { EnrollmentType, StudentType, Unenrollment as UnenrollmentType } from '@wonderschool/common-base-types';
import {
  Badge,
  Button,
  ButtonLink,
  ColorPaletteEnum,
  Dialog,
  Input,
  MainContentLayout,
  Option,
  Select,
  showToast,
  TextArea,
  Toast,
  ToastTypeEnum,
} from '@wonderschool/common-base-ui';
import { useParentHandbookQuery } from '@wonderschool/common-food-program-data-access';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { firestore } from '../../../api/firebase/firebase';
import { saveDocument } from '../../../api/firebase/firestore';
import { fetchOrganizationInvoicePlans, paths as invoicePaths } from '../../../api/firebase/invoices';
import { getTitlePrefix } from '../../../config/env';
import { EnrollmentDeposit, EnrollmentFormDataType, EnrollmentStatusEnum } from '../../../enrollments';
import { dateFormatter, toDateObject } from '../../../helpers/dates';
import { useOrganization } from '../../../hooks/useOrganizations';
import { useMarketplaceAPI } from '../../../integrations';
import { paths } from '../../../navigation/paths';
import { studentSelected } from '../../../redux/actions/studentsActions';
import { logError } from '../../../rollbar';
import {
  useSelectedStudent,
  useStudentEnrollment,
  useStudentOrPrimaryLocation,
  useStudents,
} from '../../studentsHooks';
import { unenrollmentInitiatedByOptions, unenrollmentReasonOptions } from './StudentFormUnenrollmentReasons';
import {
  ActivePlan,
  DEPOSIT_PICK_MANUAL,
  DEPOSIT_PICK_NONE,
  DepositSection,
  PlansPicker,
  SubmitSummary,
} from './UnenrollDialogSections';
import {
  bestDepositCandidate,
  buildDepositCandidates,
  calcProration,
  computeDailyTuitionCents,
  DAYS_PER_MONTH,
  DepositCandidate,
  fmtCents,
  fmtShortDate,
  guessMonthlyTuitionCents,
  schoolTimezoneUtcMidnight,
  studentFullName,
  unenrolledNoticeFields,
} from './unenrollHelpers';

export function buildPlanSnapshot(
  picked: string[],
  allPlans: { id?: string; dateEnd?: number | string | null }[]
): { id: string; previousDateEnd: number | null }[] {
  return picked.map((id) => {
    const raw = allPlans.find((p) => p.id === id)?.dateEnd ?? null;
    const previousDateEnd = typeof raw === 'string' ? Number(raw) || null : raw;
    return { id, previousDateEnd };
  });
}

// End dates owned by the provider ("End plan") or by a school closure (MP-5688) must survive a
// cancelled unenrollment. Mirrors OWNED_END_PLAN_SOURCES in
// functions/src/api/invoicePlans-ts.ts, which guards the server-side clean-up the same way.
const OWNED_END_PLAN_SOURCES = ['provider', 'school_closed'];

/** The snapshot entries whose pre-unenrollment `dateEnd` may be restored on cancel. */
export function selectPlanEndRestores<T extends { id: string }>(
  snapshot: T[],
  currentPlans: { id: string; endPlanSource?: string | null }[]
): T[] {
  return snapshot.filter(({ id }) => {
    const source = currentPlans.find((plan) => plan.id === id)?.endPlanSource;
    return !source || !OWNED_END_PLAN_SOURCES.includes(source);
  });
}

type UnenrollErrors = {
  unenrollmentDate?: string;
  unenrollmentInitiatedBy?: string;
  unenrollmentReason?: string;
};

interface UnenrollPageRouteParams {
  studentId?: string;
  [key: string]: string | undefined;
}

export function UnenrollPage() {
  const { studentId } = useParams<UnenrollPageRouteParams>();
  const { list: students } = useStudents();
  const selectedStudent = useSelectedStudent();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Bootstrap: if the user deep-linked here (or refreshed), the redux
  // slice may not know which student is "selected". Match on the URL
  // param and dispatch — same pattern as StudentEditPage.
  const foundStudent = students.find((s) => s.id === studentId);
  const selectedStudentId = selectedStudent?.id;
  useEffect(() => {
    if (foundStudent && foundStudent.id !== selectedStudentId) {
      dispatch(studentSelected(foundStudent));
    }
  }, [foundStudent, selectedStudentId, dispatch]);

  // students.length > 0 means the list has loaded; if no match exists at
  // that point the studentId is genuinely invalid (bad URL / deleted record).
  const studentNotFound = !studentId || (students.length > 0 && !foundStudent);

  if (studentNotFound) {
    return (
      <>
        <UnenrollPageTitle />
        <MainContentLayout>
          <div className="mx-auto max-w-md px-4 py-12 text-center">
            <p className="text-sm text-slate-500">{t('enrollments.unenrollment.studentNotFound')}</p>
            <button
              type="button"
              onClick={() => navigate(paths.students)}
              className="mt-4 text-sm text-blue-700 hover:text-blue-900"
            >
              {t('enrollments.unenrollment.backToStudents')}
            </button>
          </div>
        </MainContentLayout>
      </>
    );
  }

  return (
    <>
      <UnenrollPageTitle />
      <MainContentLayout>
        <UnenrollPageBody
          key={studentId}
          studentId={studentId}
          navigateBackToStudent={() => navigate(paths.studentDetails.replace(':studentId', studentId))}
        />
      </MainContentLayout>
    </>
  );
}

function UnenrollPageTitle() {
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>
        {getTitlePrefix()}
        {t('enrollments.unenrollStudent')}
      </title>
      <body />
    </Helmet>
  );
}

interface UnenrollPageBodyProps {
  studentId: string;
  navigateBackToStudent: () => void;
}

function UnenrollPageBody({ studentId, navigateBackToStudent }: Readonly<UnenrollPageBodyProps>) {
  const { t } = useTranslation();
  const selectedStudent = useSelectedStudent();

  // On deep-link / refresh the parent's bootstrap effect hasn't
  // dispatched yet, so redux returns null. useStudentEnrollment
  // dereferences the student and would throw — render a lightweight
  // loading state until redux warms up. The parent's effect runs
  // in the same render tree, so this resolves within one tick.
  if (!selectedStudent) {
    return (
      <div className="mx-auto max-w-md px-4 py-12 text-center text-sm text-slate-500">
        {t('enrollments.unenrollment.loadingStudent')}
      </div>
    );
  }

  // Guard: if an unenrollment is already recorded, show a read-only
  // summary instead of the editable form. Re-submitting before the cron
  // runs would overwrite the notice date and change the money in-flight.
  if ((selectedStudent as any).enrollment?.unenrolled?.date) {
    return <UnenrollPendingView selectedStudent={selectedStudent} navigateBackToStudent={navigateBackToStudent} />;
  }

  return (
    <UnenrollPageForm
      studentId={studentId}
      selectedStudent={selectedStudent}
      navigateBackToStudent={navigateBackToStudent}
    />
  );
}

interface UnenrollPendingViewProps {
  selectedStudent: StudentType;
  navigateBackToStudent: () => void;
}

export function UnenrollPendingView({ selectedStudent, navigateBackToStudent }: Readonly<UnenrollPendingViewProps>) {
  const { t } = useTranslation();
  const organization = useOrganization();
  const dispatch = useDispatch();
  const { sendEnrollmentStatus } = useMarketplaceAPI();
  const enrollment = (selectedStudent as any).enrollment;
  const unenrolled = enrollment?.unenrolled;

  const lastDay = toDateObject(unenrolled?.date) ?? null;
  const noticeGivenOn = toDateObject(unenrolled?.noticeGivenOn) ?? undefined;
  const noticeDaysApplied: number | undefined = unenrolled?.noticeDaysApplied;
  const monthlyTuitionCents: number =
    unenrolled?.monthlyTuitionCents ?? enrollment?.tuitionAndFees?.tuition?.amount ?? 0;

  const depositCandidate = bestDepositCandidate(buildDepositCandidates(selectedStudent));
  const depositHeldCents = depositCandidate?.refundable ? depositCandidate.amountCents : 0;

  const primaryLocation = useStudentOrPrimaryLocation(selectedStudent);
  // Prefer the interval-correct daily rate stored at submission time (new records).
  // Number.isFinite guards against persisted non-numeric values (type drift,
  // bad migration) that would survive the ?? check and produce NaN proration.
  // Fall back to the monthly round-trip for old records that pre-date CCMS-8118.
  const storedDailyTuitionCents: number = Number.isFinite(unenrolled?.dailyTuitionCents)
    ? unenrolled.dailyTuitionCents
    : monthlyTuitionCents / DAYS_PER_MONTH;

  const lastDayStr = lastDay ? lastDay.toISOString().slice(0, 10) : '';
  const proration =
    lastDayStr && noticeDaysApplied !== undefined
      ? calcProration(
          storedDailyTuitionCents,
          noticeDaysApplied,
          depositHeldCents,
          lastDayStr,
          noticeGivenOn,
          schoolTimezoneUtcMidnight(primaryLocation?.timezone)
        )
      : null;

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancelUnenrollment = useCallback(async () => {
    if (!organization?.id) return;
    try {
      setIsCancelling(true);
      // planSnapshot (new) carries each plan's previousDateEnd so we can
      // restore the exact pre-unenrollment value. Fall back to the legacy
      // planIds array for records scheduled before this change — those plans
      // had no prior dateEnd set (the old code assumed null), so null is
      // still the correct restore value for them.
      const planSnapshot: { id: string; previousDateEnd?: number | null }[] = (unenrolled as any)?.planSnapshot ?? [];
      const legacyPlanIds: string[] = (unenrolled as any)?.planIds ?? [];
      const snapshotToRestore =
        planSnapshot.length > 0 ? planSnapshot : legacyPlanIds.map((id) => ({ id, previousDateEnd: null }));
      // Re-read each plan: one ended by the provider or by a school closure since the unenrollment
      // was scheduled keeps its end date. Restoring `null` there would bill the family forever.
      const currentPlans = await Promise.all(
        snapshotToRestore.map(async ({ id }) => {
          const planDoc = await firestore().collection(invoicePaths.invoicePlans(organization.id)).doc(id).get();
          return { id, endPlanSource: planDoc.data()?.endPlanSource ?? null };
        })
      );
      const plansToRestore = selectPlanEndRestores(snapshotToRestore, currentPlans);

      // Mirror the form's atomic batch: student write + plan dateEnd restores
      // commit together so we never leave the student un-unenrolled while
      // plans still have their dateEnd pointing at the old last day.
      const batch = firestore().batch() as any;

      // Use batch.update with dot-notation + FieldValue.delete() to remove the
      // nested unenrolled field. saveDocument calls set() which rejects undefined.
      const studentDocRef = firestore().collection(`organizations/${organization.id}/students`).doc(selectedStudent.id);
      batch.update(studentDocRef, {
        enrollmentStatus: true,
        organization: organization.id,
        'enrollment.status': EnrollmentStatusEnum.ENROLLED,
        'enrollment.unenrolled': firestore.FieldValue.delete(),
      });

      // Restore each plan's dateEnd to the value it had before the unenrollment
      // was scheduled. planSnapshot records the exact pre-unenrollment dateEnd so
      // we don't accidentally open plans that were already set to end on a
      // specific date unrelated to this unenrollment.
      await Promise.all(
        plansToRestore.map(({ id, previousDateEnd }) =>
          saveDocument({
            collectionPath: invoicePaths.invoicePlans(organization.id),
            data: { id, dateEnd: previousDateEnd ?? null },
            batch,
          })
        )
      );

      await batch.commit();

      // Restore the Marketplace lead status to ENROLLED so it stays in sync
      // with Firestore. Scheduling sends UNENROLLED; cancelling must reverse it.
      const interestedId = (
        selectedStudent?.enrollment as unknown as
          | {
              interestedFamily?: { id?: string };
            }
          | undefined
      )?.interestedFamily?.id;
      if (interestedId) {
        await sendEnrollmentStatus(EnrollmentStatusEnum.ENROLLED, interestedId);
      }

      dispatch(
        studentSelected({
          ...(selectedStudent satisfies StudentType),
          enrollmentStatus: true,
          enrollment: {
            ...(selectedStudent.enrollment || {}),
            status: EnrollmentStatusEnum.ENROLLED,
            unenrolled: undefined,
          } as EnrollmentType,
        })
      );
      setShowCancelConfirm(false);
      showToast(
        ToastTypeEnum.Success,
        t('enrollments.unenrollment.pending.cancelSuccess', { name: studentFullName(selectedStudent) })
      );
      navigateBackToStudent();
    } catch (e) {
      console.error('[UnenrollPage] cancel failed', e);
      showToast(ToastTypeEnum.Error, t('enrollments.unenrollment.submitError'));
    } finally {
      setIsCancelling(false);
    }
  }, [dispatch, navigateBackToStudent, organization?.id, selectedStudent, sendEnrollmentStatus, t, unenrolled]);

  // Allow cancellation only while the last day is still in the future AND the
  // student is still ENROLLED. The date alone is insufficient: the cron may not
  // have run yet when lastDay === today, so we guard on the status flip too —
  // once the cron marks the student UNENROLLED the button disappears immediately.
  const canCancel = lastDay && lastDay > new Date() && enrollment?.status === EnrollmentStatusEnum.ENROLLED;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <ButtonLink
        onClick={navigateBackToStudent}
        icon={<ChevronLeftIcon className="size-4" />}
        iconDirection="left"
        content={t('enrollments.unenrollment.pending.backToStudent')}
        extraClasses="mb-4"
      />

      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">{t('enrollments.unenrollment.pending.banner')}</p>
        <p className="mt-1 text-sm text-amber-700">{t('enrollments.unenrollment.pending.bannerSubtitle')}</p>
      </div>

      {proration?.willRefund && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            {t('enrollments.unenrollment.pending.depositAdvisory')}
          </p>
          <p className="mt-1 text-sm text-amber-700">
            {t('enrollments.unenrollment.pending.depositAdvisoryNote', {
              amount: fmtCents(-proration.netOwedCents),
            })}
          </p>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
        <h2 className="mb-4 text-base font-semibold text-slate-900">
          {t('enrollments.unenrollment.pending.detailsTitle')}
        </h2>
        <dl className="divide-y divide-slate-100">
          <div className="flex justify-between py-3">
            <dt className="text-sm text-slate-500">{t('enrollments.unenrollment.pending.lastDay')}</dt>
            <dd className="text-sm font-medium text-slate-900">{lastDay ? fmtShortDate(lastDay) : '—'}</dd>
          </div>
          <div className="flex justify-between py-3">
            <dt className="text-sm text-slate-500">{t('enrollments.unenrollment.pending.noticeFiled')}</dt>
            <dd className="text-sm font-medium text-slate-900">{noticeGivenOn ? fmtShortDate(noticeGivenOn) : '—'}</dd>
          </div>
          {proration && (
            <div className="flex justify-between py-3">
              <dt className="text-sm text-slate-500">{t('enrollments.unenrollment.pending.noticeStatus')}</dt>
              <dd>
                {proration.meetsNotice ? (
                  <Badge label={t('unenrollments.dashboard.noticeMet')} color={ColorPaletteEnum.GREEN} />
                ) : (
                  <Badge label={t('unenrollments.dashboard.noticeShort')} color={ColorPaletteEnum.YELLOW} />
                )}
              </dd>
            </div>
          )}
          {proration && !proration.meetsNotice && proration.prorateAmountCents > 0 && (
            <div className="flex justify-between py-3">
              <dt className="text-sm text-slate-500">{t('enrollments.unenrollment.pending.shortNoticeFee')}</dt>
              <dd className="text-sm font-medium text-rose-700">{fmtCents(proration.prorateAmountCents)}</dd>
            </div>
          )}
          {depositHeldCents > 0 && proration && (
            <div className="flex justify-between py-3">
              <dt className="text-sm text-slate-500">{t('enrollments.depositHeld')}</dt>
              <dd className="text-sm font-medium text-slate-900">{fmtCents(depositHeldCents)}</dd>
            </div>
          )}
          {depositHeldCents > 0 && proration && proration.depositCreditCents > 0 && (
            <div className="flex justify-between py-3">
              <dt className="text-sm text-slate-500">{t('enrollments.depositAppliedToFee')}</dt>
              <dd className="text-sm text-slate-500">{`− ${fmtCents(proration.depositCreditCents)}`}</dd>
            </div>
          )}
          {proration && proration.netOwedCents !== 0 && (
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-3">
              <dt className="text-sm font-medium text-slate-700">{t('enrollments.netOwed')}</dt>
              <dd
                className={`text-sm font-semibold ${proration.netOwedCents < 0 ? 'text-amber-700' : 'text-green-700'}`}
              >
                {fmtCents(Math.abs(proration.netOwedCents))} (
                {t(proration.netOwedCents < 0 ? 'enrollments.owedToFamily' : 'enrollments.owedToSchool')})
              </dd>
            </div>
          )}
        </dl>
      </div>

      {canCancel && (
        <div className="mt-6">
          <Button
            onClick={() => setShowCancelConfirm(true)}
            label={t('enrollments.unenrollment.pending.cancelAction')}
          />
        </div>
      )}

      <Dialog isOpen={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} hasCloseButton fullScreenOnMobile>
        <Dialog.Panel>
          <Dialog.Title>{t('enrollments.unenrollment.pending.cancelConfirmTitle')}</Dialog.Title>
          <Dialog.Description>
            {t('enrollments.unenrollment.pending.cancelConfirmBody', { name: studentFullName(selectedStudent) })}
          </Dialog.Description>
        </Dialog.Panel>
        <Dialog.Actions>
          <div className="grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-2">
            <Button
              onClick={() => setShowCancelConfirm(false)}
              label={t('common.cancel')}
              extraClasses="w-full order-2 sm:order-1"
            />
            <Button
              onClick={handleCancelUnenrollment}
              primary
              label={t('enrollments.unenrollment.pending.cancelConfirmButton')}
              disabled={isCancelling}
              loading={isCancelling}
              extraClasses="w-full order-1 sm:order-2"
            />
          </div>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
}

interface UnenrollPageFormProps extends UnenrollPageBodyProps {
  selectedStudent: StudentType;
}

function UnenrollPageForm({ navigateBackToStudent, selectedStudent }: Readonly<UnenrollPageFormProps>) {
  const { t } = useTranslation();
  const organization = useOrganization();
  const primaryLocation = useStudentOrPrimaryLocation(selectedStudent);
  const { enrollmentDate, unenrollmentData } = useStudentEnrollment(selectedStudent);
  const { sendEnrollmentStatus } = useMarketplaceAPI();
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<EnrollmentFormDataType>({});
  const [errors, setErrors] = useState<UnenrollErrors>({});

  // ── Cancellation-period state ─────────────────────────────────────────
  const { data: handbookData, loading: handbookLoading, error: handbookError } = useParentHandbookQuery();
  const handbookNoticeDays = handbookData?.parentHandbook?.minNoticeDays ?? undefined;

  // A failed lookup (e.g. "Organization marketplaceSchoolId not found") and
  // "no policy configured" both leave handbookNoticeDays undefined and omit
  // the notice fields from the write — log the failure so the broken-org
  // case stays visible (CCMS-8186).
  useEffect(() => {
    if (handbookError) {
      logError('unenroll: parent handbook lookup failed — notice fields will be omitted', handbookError, {
        organizationId: organization?.id,
      });
    }
  }, [handbookError, organization?.id]);
  const [noticeDays, setNoticeDays] = useState<number | undefined>(handbookNoticeDays);
  const noticeOverridden = noticeDays !== handbookNoticeDays;

  // ── Plans state ───────────────────────────────────────────────────────
  const [allPlans, setAllPlans] = useState<ActivePlan[]>([]);
  const [studentsById, setStudentsById] = useState<Record<string, Partial<StudentType>>>({});
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [selectedPlanIds, setSelectedPlanIds] = useState<Array<string | null>>([null]);

  // ── Deposit picker state ──────────────────────────────────────────────
  const [depositPickId, setDepositPickId] = useState<string>('');
  const [manualAmount, setManualAmount] = useState<string>('0.00');
  const [manualRefundable, setManualRefundable] = useState<boolean>(true);

  // Prefill from current student on mount. Unlike the old dialog we
  // don't need to reset transient state on close — a fresh mount is a
  // fresh state.
  useEffect(() => {
    setFormData({
      unenrollmentInitiatedBy: unenrollmentData?.initiatedBy ?? '',
      unenrollmentReason: unenrollmentData?.reason ?? '',
      unenrollmentDate: unenrollmentData?.date ? (dateFormatter(unenrollmentData.date) ?? '') : '',
      unenrollmentNotes: unenrollmentData?.notes ?? '',
    });
    setNoticeDays(handbookNoticeDays);
  }, [unenrollmentData, handbookNoticeDays]);

  // ── Load active plans + their linked students once on mount ─────────
  useEffect(() => {
    if (!organization?.id) return;
    let cancelled = false;
    const loadPlans = async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        // Fetch all plans for the org and filter status in-memory.
        // The composite (status == active, orderBy dateStart) index isn't
        // created in every project — and the active set is small enough
        // that an unfiltered scan + JS filter is cheaper than provisioning
        // the index everywhere. Same trick the lego port uses.
        const res = await fetchOrganizationInvoicePlans(organization.id, []);
        const plans = ((res?.list ?? []) as ActivePlan[]).filter((p) => (p as any).status === 'active');
        if (cancelled) return;
        setAllPlans(plans);
        const sid = selectedStudent?.id;
        const studentPlans = sid ? plans.filter((p) => (p.students || []).some((s) => s.id === sid)) : [];
        setSelectedPlanIds(studentPlans.length > 0 ? studentPlans.map((p) => p.id) : [null]);
        const seed: Record<string, Partial<StudentType>> = {};
        if (sid && selectedStudent) seed[sid] = selectedStudent;
        setStudentsById(seed);
      } catch (e: any) {
        if (!cancelled) setPlansError(e?.message || String(e));
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [organization?.id, selectedStudent]);

  // ── Derived: deposit candidates + resolved deposit + money preview ──
  const depositCandidates = useMemo<DepositCandidate[]>(
    () => buildDepositCandidates(selectedStudent ?? null),
    [selectedStudent]
  );

  const effectiveDepositPickId = useMemo<string>(() => {
    const valid = new Set<string>([DEPOSIT_PICK_NONE, DEPOSIT_PICK_MANUAL, ...depositCandidates.map((c) => c.id)]);
    if (depositPickId && valid.has(depositPickId)) return depositPickId;
    const best = bestDepositCandidate(depositCandidates);
    return best ? best.id : DEPOSIT_PICK_NONE;
  }, [depositPickId, depositCandidates]);

  const resolvedDeposit = useMemo<{
    amountCents: number;
    refundable: boolean;
    source: string;
  } | null>(() => {
    if (effectiveDepositPickId === DEPOSIT_PICK_NONE) return null;
    if (effectiveDepositPickId === DEPOSIT_PICK_MANUAL) {
      const cents = Math.round((parseFloat(manualAmount) || 0) * 100);
      return { amountCents: cents, refundable: manualRefundable, source: 'manual' };
    }
    const c = depositCandidates.find((x) => x.id === effectiveDepositPickId);
    return c ? { amountCents: c.amountCents, refundable: c.refundable, source: c.source } : null;
  }, [effectiveDepositPickId, depositCandidates, manualAmount, manualRefundable]);

  const monthlyTuitionCents = useMemo(
    () => guessMonthlyTuitionCents(selectedStudent ?? null, allPlans),
    [selectedStudent, allPlans]
  );

  const dailyTuitionCents = useMemo(
    // Fall back to the monthly round-trip for legacy students whose tuition
    // lives only on enrollment.tuitionAndFees (no active plans). Matches the
    // same fallback used by the pending view and the dashboard.
    () => computeDailyTuitionCents(allPlans, selectedStudent?.id ?? '') || monthlyTuitionCents / DAYS_PER_MONTH,
    [allPlans, selectedStudent?.id, monthlyTuitionCents]
  );

  const proration = useMemo(() => {
    const depositHeldCents = resolvedDeposit?.refundable ? resolvedDeposit.amountCents : 0;
    const schoolToday = schoolTimezoneUtcMidnight(primaryLocation?.timezone);

    // When re-opening an existing unenroll with the same last day, anchor
    // noticeGivenDate to the stored value rather than "today" — otherwise the
    // preview would show a short-notice warning for a notice given weeks ago,
    // even though the cron will use the original date and charge nothing.
    const existingUnenrolled = (selectedStudent as any).enrollment?.unenrolled;
    const selectedLastDayStr = formData.unenrollmentDate
      ? (toDateObject(formData.unenrollmentDate)?.toISOString().slice(0, 10) ?? '')
      : '';
    const existingLastDayStr = toDateObject(existingUnenrolled?.date)?.toISOString().slice(0, 10);
    const noticeGivenDate =
      existingLastDayStr && existingLastDayStr === selectedLastDayStr
        ? (toDateObject(existingUnenrolled?.noticeGivenOn) ?? schoolToday)
        : schoolToday;

    return calcProration(
      dailyTuitionCents,
      noticeDays ?? 0,
      depositHeldCents,
      selectedLastDayStr,
      noticeGivenDate,
      schoolToday
    );
  }, [dailyTuitionCents, noticeDays, resolvedDeposit, formData.unenrollmentDate, primaryLocation, selectedStudent]);

  const translatedErrors = useMemo(
    () =>
      Object.entries(errors)
        .map(([key, value]) => ({ [key]: value ? t(value) : undefined }))
        .reduce((acc, curr) => Object.assign(acc, curr), {} as Record<string, string | undefined>),
    [errors, t]
  );

  const lastDayDate = useMemo(
    () => (formData.unenrollmentDate ? toDateObject(formData.unenrollmentDate) : null),
    [formData.unenrollmentDate]
  );

  // ── Validation ────────────────────────────────────────────────────────
  const validate = useCallback((): boolean => {
    const nextErrors: UnenrollErrors = {};
    if (!formData.unenrollmentDate) {
      nextErrors.unenrollmentDate = 'students.enrollment.validation.unenrollmentDateRequired';
    }
    if (formData.unenrollmentDate && enrollmentDate) {
      const unenroll = toDateObject(formData.unenrollmentDate);
      const enroll = toDateObject(enrollmentDate);
      if (unenroll && enroll) {
        const isSameDay = unenroll.toDateString() === enroll.toDateString();
        const isBefore = unenroll.getTime() < enroll.getTime();
        if (isSameDay || isBefore) {
          nextErrors.unenrollmentDate = 'students.enrollment.validation.unenrollmentDateMustBeAfterEnrollmentDate';
        }
      }
    }
    setErrors(nextErrors);
    return isEmpty(nextErrors);
  }, [formData.unenrollmentDate, enrollmentDate]);

  // ── Submit ───────────────────────────────────────────────────────────
  const handleUnenroll = useCallback(async () => {
    if (!selectedStudent || !organization?.id) return;
    // The submit button is disabled while the handbook query is in flight,
    // but guard here too: submitting before it resolves would silently omit
    // the notice fields for a provider who does have a policy.
    if (handbookLoading) return;
    if (!validate()) return;

    try {
      setIsSaving(true);
      const selectedDate = formData.unenrollmentDate ? new Date(formData.unenrollmentDate) : null;
      const now = new Date();
      const shouldRemainEnrolled = !!selectedDate && selectedDate > now;
      const newStatus = shouldRemainEnrolled ? EnrollmentStatusEnum.ENROLLED : EnrollmentStatusEnum.UNENROLLED;
      const lastDay = toDateObject(formData.unenrollmentDate!);
      if (!lastDay) return; // validate() ensures the date is set; toDateObject returning null here is unexpected

      // Preserve noticeGivenOn only when the last day is unchanged — if the
      // director moves the last day, the notice period restarts from today.
      // schoolTimezoneUtcMidnight anchors "today" to the school's calendar day
      // (UTC midnight) so both the form and the cron agree when using UTC accessors.
      const existingUnenrolled = selectedStudent.enrollment?.unenrolled as any;
      const existingLastDayStr = toDateObject(existingUnenrolled?.date)?.toISOString().slice(0, 10);
      const noticeGivenOn =
        existingLastDayStr && existingLastDayStr === lastDay.toISOString().slice(0, 10)
          ? (toDateObject(existingUnenrolled?.noticeGivenOn) ?? schoolTimezoneUtcMidnight(primaryLocation?.timezone))
          : schoolTimezoneUtcMidnight(primaryLocation?.timezone);

      const picked = selectedPlanIds.filter((id): id is string => Boolean(id));

      const unenrolled: UnenrollmentType = {
        initiatedBy: formData.unenrollmentInitiatedBy ?? '',
        reason: formData.unenrollmentReason ?? '',
        date: lastDay,
        notes: formData.unenrollmentNotes ?? '',
        ...({
          // Omitted entirely when the handbook provided no notice period —
          // this Firestore app rejects undefined field values (CCMS-8186).
          ...unenrolledNoticeFields(noticeDays, handbookNoticeDays),
          // noticeGivenOn is the authoritative from-date for prorate math in
          // the cron (unenrollStudentsOnDate). Reset to now when the last day
          // changes so the cron uses the actual re-notice date.
          noticeGivenOn,
          // Snapshot the tuition the director saw at submission time. The
          // dashboard can't load recurring plans for all students, so without
          // this it falls back to the raw tuition field and shows '—' for
          // students whose tuition lives only in recurring plans.
          monthlyTuitionCents,
          // Persist the interval-correct daily rate so the pending view can
          // display the exact same proration the director saw on the form,
          // without re-deriving it via the monthly round-trip approximation.
          dailyTuitionCents,
          // Persist the money summary so the student profile and any future
          // audit surface can read it without recomputing from raw inputs.
          // Stored at submit time when all inputs are known; the cron preserves
          // it unchanged via the enrollment spread when it flips the status.
          ...(proration && {
            settlementAdvisory: {
              netOwedCents: proration.netOwedCents,
              prorateAmountCents: proration.prorateAmountCents,
              depositHeldCents: proration.depositHeldCents,
              depositAppliedCents: proration.depositCreditCents,
              // With no notice policy there is no requirement to meet — store
              // absence rather than a vacuous true, matching the dashboard's
              // "—" for these records. The profile's short-notice line already
              // guards on prorateAmountCents > 0, which is 0 without a policy.
              ...(noticeDays !== undefined && { meetsNotice: proration.meetsNotice }),
            },
          }),
          // Snapshot each plan's previous dateEnd alongside its ID so the
          // cancel action can restore the exact pre-unenrollment value. Storing
          // only IDs (the old planIds approach) assumed every plan had no prior
          // end date — wrong for plans that already had a dateEnd set before the
          // unenrollment was scheduled.
          ...(picked.length > 0 && { planSnapshot: buildPlanSnapshot(picked, allPlans) }),
        } as Partial<UnenrollmentType>),
      };

      // Cast because the spread of `EnrollmentType | {}` widens required
      // fields (like automaticallyEnroll) back to optional. We know the
      // spread source is either a real EnrollmentType or empty, and we
      // set status + unenrolled explicitly — the runtime shape is fine.
      const enrollment = {
        ...(selectedStudent.enrollment || {}),
        status: newStatus,
        unenrolled,
      } as EnrollmentType;

      // Promote a non-structured deposit onto enrollment.deposit so the
      // deposit is consistently readable by the dashboard and any future
      // re-unenroll. Structured picks are left alone — they ARE the canonical record.
      const enrollmentWithDeposit = enrollment as unknown as {
        deposit?: EnrollmentDeposit;
      };
      if (
        resolvedDeposit &&
        resolvedDeposit.amountCents > 0 &&
        effectiveDepositPickId !== 'structured' &&
        !enrollmentWithDeposit.deposit
      ) {
        const promoted: EnrollmentDeposit = {
          amountCents: resolvedDeposit.amountCents,
          refundable: resolvedDeposit.refundable,
        };
        enrollmentWithDeposit.deposit = promoted;
      }

      const data: Partial<StudentType> = {
        id: selectedStudent.id,
        enrollmentStatus: newStatus === EnrollmentStatusEnum.ENROLLED,
        enrollment,
      };

      // Atomic write: the student status flip and every plan's dateEnd
      // go into a single Firestore WriteBatch and commit together. If any
      // one write would fail, none land — so we never leave the student
      // flipped to UNENROLLED while a plan the UI promised would "stop on
      // the last day" keeps billing. (The old code did an independent
      // saveStudent + Promise.all of per-plan writes; a partial failure
      // there stranded active plans on an unenrolled student.)
      const batch = firestore().batch() as any;

      await saveDocument({
        collectionPath: `organizations/${organization.id}/students`,
        data: { ...data, organization: organization.id },
        batch,
      });

      // End or update each picked plan. Single-student plans get dateEnd stamped;
      // multi-student plans have this student removed from students[] so the plan
      // continues billing the remaining students. Mirrors what
      // updateInvoicePlansEndDateForStudent does server-side.
      if (picked.length > 0) {
        const dateEndMillis = (() => {
          const d = new Date(lastDay);
          d.setHours(0, 0, 0, 0);
          return d.getTime();
        })();
        await Promise.all(
          picked.map((planId) => {
            const plan = allPlans.find((p) => p.id === planId);
            const planStudents = plan?.students ?? [];
            if (planStudents.length > 1) {
              // Multi-student plan: remove this student. Record in removedStudentIds so
              // clearInvoicePlansEndDateForStudent can restore them if cancel is requested.
              const updatedStudents = planStudents.filter((s) => s.id !== selectedStudent?.id);
              const planDocRef = firestore().collection(invoicePaths.invoicePlans(organization.id)).doc(planId);
              batch.update(planDocRef, {
                students: updatedStudents,
                removedStudentIds: firestore.FieldValue.arrayUnion(selectedStudent?.id),
              });
              return Promise.resolve();
            } else {
              // Single-student plan: stamp dateEnd + unenrollmentDate
              return saveDocument({
                collectionPath: invoicePaths.invoicePlans(organization.id),
                data: { id: planId, dateEnd: dateEndMillis, unenrollmentDate: dateEndMillis },
                batch,
              });
            }
          })
        );
      }

      await batch.commit();

      const updatedStudent: StudentType = {
        ...selectedStudent,
        enrollmentStatus: data.enrollmentStatus ?? selectedStudent.enrollmentStatus,
        enrollment: {
          ...(selectedStudent.enrollment || {}),
          ...(data.enrollment || {}),
        } as EnrollmentType,
      };
      dispatch(studentSelected(updatedStudent));

      // interestedFamily lives on the local EnrollmentType (src/enrollments)
      // but isn't declared on common-base-types' EnrollmentType — cast
      // through unknown so we can read the marketplace-lead id without
      // being blocked on a common-base-types release.
      const interestedId = (
        selectedStudent?.enrollment as unknown as
          | {
              interestedFamily?: { id?: string };
            }
          | undefined
      )?.interestedFamily?.id;
      if (interestedId) {
        await sendEnrollmentStatus(EnrollmentStatusEnum.UNENROLLED, interestedId);
      }

      showToast(ToastTypeEnum.Success, t('enrollments.unenrollment.successToast', { studentName }));
    } catch (e) {
      console.error('[UnenrollPage] submit failed', e);
      showToast(ToastTypeEnum.Error, t('enrollments.unenrollment.submitError'));
    } finally {
      setIsSaving(false);
    }
  }, [
    allPlans,
    dispatch,
    effectiveDepositPickId,
    formData,
    handbookLoading,
    handbookNoticeDays,
    noticeDays,
    organization?.id,
    primaryLocation,
    proration,
    resolvedDeposit,
    selectedPlanIds,
    selectedStudent,
    sendEnrollmentStatus,
    t,
    validate,
  ]);

  // ── Rendering helpers ────────────────────────────────────────────────
  const studentName = (selectedStudent as any)?.displayName || studentFullName(selectedStudent);
  const orgName = (organization as any)?.name || 'school';

  const selectedPlans = selectedPlanIds
    .map((id) => (id ? allPlans.find((p) => p.id === id) : null))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  // Glyphs (✓ / ⚠) are rendered as separate styled spans rather than
  // baked into the translated string, so translators get clean sentences
  // and the semantic marker can be restyled independently. Pluralization
  // uses i18next count-based plural forms (the `_one` / `_other` keys in
  // the locale files) instead of a hand-rolled `s` suffix, which is wrong
  // for non-English plural rules.
  const daysNoticeHint = lastDayDate ? (
    proration.meetsNotice ? (
      <p className="mt-1 text-xs text-emerald-700">
        {/* eslint-disable-next-line i18next/no-literal-string -- decorative, aria-hidden status glyph; not translated copy */}
        <span aria-hidden="true">✓ </span>
        <Trans
          i18nKey="enrollments.unenrollment.noticeMet"
          values={{ count: proration.daysNotice }}
          components={{ b: <b /> }}
        />
      </p>
    ) : (
      <p className="mt-1 text-xs text-amber-700">
        <span aria-hidden="true">⚠ </span>
        <Trans
          i18nKey="enrollments.unenrollment.noticeShort"
          values={{
            count: proration.daysNotice,
            noticeDays: noticeDays ?? 0,
            shortNoticeFee: fmtCents(proration.prorateAmountCents),
          }}
          components={{ b: <b /> }}
        />
      </p>
    )
  ) : null;

  const pastDateWarning =
    proration.isPastDated && lastDayDate ? (
      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
        <span aria-hidden="true">⚠ </span>
        <Trans
          i18nKey="enrollments.unenrollment.pastDated"
          values={{ count: proration.daysPastDated, noticeDays: noticeDays ?? 0 }}
          components={{ b: <b /> }}
        />
      </div>
    ) : null;

  const noticeOverrideHint = noticeOverridden ? (
    <p className="mt-1 text-xs text-amber-700">
      <Trans
        i18nKey="enrollments.unenrollment.noticeOverridden"
        values={{ count: handbookNoticeDays }}
        components={{ b: <b /> }}
      />
    </p>
  ) : (
    <p className="mt-1 text-xs text-slate-500">{t('enrollments.unenrollment.handbookNotice.label', { orgName })}</p>
  );

  const initiatedByOpts: Option[] = unenrollmentInitiatedByOptions.map((key) => ({
    value: t(key),
    label: t(key),
  }));
  const reasonOpts: Option[] = unenrollmentReasonOptions.map((key) => ({
    value: t(key),
    label: t(key),
  }));

  // ── Form step ────────────────────────────────────────────────────────
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <Toast />
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={navigateBackToStudent}
            className="mb-1 text-sm text-slate-500 hover:text-slate-700"
          >
            {t('enrollments.unenrollment.backToStudent.label', {
              studentName: studentName || t('enrollments.unenrollment.studentFallback'),
            })}
          </button>
          <h1 className="text-2xl font-semibold text-slate-900">
            {studentName
              ? t('enrollments.unenrollment.pageTitle', { studentName })
              : t('enrollments.unenrollStudentTitle')}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('enrollments.unenrollment.pageSubtitle')}</p>
        </div>
      </div>

      {/* Two-column layout at ≥lg: form left (2/3), money preview right (1/3, sticky) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ── LEFT — form sections ───────────────────────────────── */}
        <div className="space-y-6 lg:col-span-2">
          {/* ── Section 1 — Unenrollment details ───────────────── */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">{t('enrollments.unenrollmentSectionLabel')}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Input
                  name="unenrollmentDate"
                  label={t('enrollments.unenrollmentDateLabel')}
                  inputType="date"
                  value={
                    formData.unenrollmentDate
                      ? (toDateObject(formData.unenrollmentDate)?.toISOString().slice(0, 10) ?? '')
                      : ''
                  }
                  required
                  onChange={(event) => {
                    const v = event.target.value;
                    const dateObj = v ? new Date(v + 'T00:00:00') : null;
                    const display = dateObj ? (dateFormatter(dateObj) ?? '') : '';
                    setFormData((prev) => ({ ...prev, unenrollmentDate: display }));
                    if (display) setErrors((prev) => omit(prev, 'unenrollmentDate'));
                  }}
                  data-testid="unenrollmentDate"
                  error={translatedErrors.unenrollmentDate}
                />
              </div>
              {handbookNoticeDays !== undefined && (
                <div>
                  <label className="block text-sm text-slate-700">
                    {t('enrollments.unenrollment.noticeDaysLabel')}
                  </label>
                  <div className="mt-1 flex">
                    <input
                      type="number"
                      min={0}
                      max={365}
                      value={noticeDays}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isFinite(v) && v >= 0) setNoticeDays(v);
                      }}
                      className="w-full rounded-l border border-slate-300 px-3 py-2 text-sm"
                      data-testid="cancellationPeriod"
                    />
                    <span className="inline-flex items-center rounded-r border border-l-0 border-slate-300 bg-slate-50 px-3 text-sm text-slate-500">
                      {t('common.days')}
                    </span>
                  </div>
                  {noticeOverrideHint}
                </div>
              )}
            </div>
            {daysNoticeHint}
            {pastDateWarning}

            <div className="mt-3">
              <Select
                label={t('enrollments.unenrollmentInitiatedByLabel')}
                name="unenrollmentInitiatedBy"
                options={initiatedByOpts}
                placeholder={t('enrollments.unenrollmentInitiatedByPlaceholder')}
                value={
                  formData.unenrollmentInitiatedBy
                    ? { value: formData.unenrollmentInitiatedBy, label: formData.unenrollmentInitiatedBy }
                    : undefined
                }
                onChange={(opt) => setFormData((prev) => ({ ...prev, unenrollmentInitiatedBy: String(opt.value) }))}
                data-testid="unenrollmentInitiatedBy"
              />
            </div>
            <div className="mt-3">
              <Select
                label={t('enrollments.unenrollmentReasonLabel')}
                name="unenrollmentReason"
                options={reasonOpts}
                placeholder={t('enrollments.unenrollmentReasonPlaceholder')}
                value={
                  formData.unenrollmentReason
                    ? { value: formData.unenrollmentReason, label: formData.unenrollmentReason }
                    : undefined
                }
                onChange={(opt) => setFormData((prev) => ({ ...prev, unenrollmentReason: String(opt.value) }))}
                data-testid="unenrollmentReason"
              />
            </div>
            <div className="mt-3">
              <TextArea
                name="unenrollmentNotes"
                label={t('enrollments.unenrollment.notesLabel')}
                rows={2}
                placeholder={t('enrollments.unenrollment.notesPlaceholder')}
                value={formData.unenrollmentNotes ?? ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, unenrollmentNotes: e.target.value }))}
                data-testid="unenrollmentNotes"
              />
            </div>
          </section>

          {/* ── Section 2 — Recurring plans to end ─────────────── */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <PlansPicker
              allPlans={allPlans}
              studentsById={studentsById}
              selectedPlanIds={selectedPlanIds}
              setSelectedPlanIds={setSelectedPlanIds}
              loading={plansLoading}
              error={plansError}
              lastDayDate={lastDayDate}
              studentId={selectedStudent?.id}
              studentName={selectedStudent?.displayName}
            />
          </section>

          {/* ── Section 3 — Deposit + money preview ────────────── */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <DepositSection
              candidates={depositCandidates}
              effectivePickId={effectiveDepositPickId}
              onPickChange={setDepositPickId}
              manualAmount={manualAmount}
              setManualAmount={setManualAmount}
              manualRefundable={manualRefundable}
              setManualRefundable={setManualRefundable}
              resolvedDeposit={resolvedDeposit}
              proration={proration}
              monthlyTuitionCents={monthlyTuitionCents}
              noticeDays={noticeDays ?? 0}
              lastDayDate={lastDayDate}
              selectedPlans={selectedPlans}
            />
          </section>
        </div>

        {/* ── RIGHT — sticky roll-up + submit ────────────────────── */}
        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <SubmitSummary
              lastDayDate={lastDayDate}
              planCount={selectedPlanIds.filter(Boolean).length}
              resolvedDeposit={resolvedDeposit}
              proration={proration}
              selectedPlans={selectedPlans}
              childFirstName={
                (selectedStudent as any)?.firstName || (selectedStudent as any)?.displayName?.split(' ')[0]
              }
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
            {/* Destructive action. Do NOT stack `primary` (which sets the
                brand bg + ring) with a raw red override — that depends on
                tailwind-merge order to win. There's no design-system
                danger variant, so we drop `primary` and set the full red
                treatment (bg + text + ring) explicitly so nothing
                brand-colored can leak through. */}
            <Button
              onClick={handleUnenroll}
              label={t('enrollments.unenrollment.endEnrollmentButton')}
              disabled={isSaving || handbookLoading || !formData.unenrollmentDate}
              loading={isSaving}
              data-testid="unenroll-confirm-btn"
              extraClasses="w-full bg-red-600 text-white ring-red-600 hover:bg-red-700 hover:ring-red-700 focus:outline-red-700"
            />
            <Button onClick={navigateBackToStudent} label={t('common.cancel')} extraClasses="w-full" />
          </div>
        </aside>
      </div>
    </div>
  );
}

export default UnenrollPage;
